// Copyright 2025 Linka Cloud  All rights reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { GoIf, GoIfElse, goIfElse, GoRange, withoutTemplate } from '@linka-cloud/go';
import AlternateEmailOutlinedIcon from '@mui/icons-material/AlternateEmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Box,
  Button,
  Divider,
  Grid,
  Icon,
  IconButton,
  InputAdornment,
  Link,
  NoSsr,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import { GetStaticProps, NextPage } from 'next';
import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Layout } from '../components/Layout';
import { useIsSubmitting } from '../hooks';
import { icons } from '../icons/icons';
import { Data, DefaultData, ExternalProviderInfoTemplate, Login, LoginTemplate } from '../openid';

export const getStaticProps: GetStaticProps<Data> = async () => {
  return {
    props: { ...DefaultData, data: LoginTemplate },
  };
};

const LoginPage: NextPage<Data<Login>> = ({ issuer, data }) => {
  const [username, setUsername] = useState(data.Username);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const invalid = withoutTemplate(data.Invalid, false);
  const providers = data.ExternalProviders;
  const toggleShowPassword = () => setShowPassword(!showPassword);
  const [isSubmitting, handleSubmit] = useIsSubmitting();

  return (
    <Layout>
      <Stack>
        <Header
          title={
            <>
              {`Sign In `}
              <Box component="span" color="primary.main">
                {issuer}
              </Box>
            </>
          }
        />
        <Stack
          component="form"
          method="post"
          action={data.PostURL}
          sx={{ display: 'flex', flexDirection: 'column' }}
          onSubmit={handleSubmit}
        >
          <Box>
            <TextField
              id={goIfElse(`eq .UsernamePrompt "username"`, 'username', 'email', data)}
              name={goIfElse('eq .UsernamePrompt "username"', 'username', 'email', data)}
              fullWidth
              label={goIfElse('eq .UsernamePrompt "username"', 'Username', 'Email', data)}
              placeholder={goIfElse('eq .UsernamePrompt "username"', 'username', 'your@email.com', data)}
              value={username}
              error={invalid && !username}
              onChange={(event) => setUsername(event.target.value.trim())}
              slotProps={{
                inputLabel: { suppressHydrationWarning: true },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <GoIfElse
                        value='eq .UsernamePrompt "username"'
                        mock={data}
                        ifv={<PersonOutlineOutlinedIcon />}
                        elsev={<AlternateEmailOutlinedIcon />}
                      />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
          <Box>
            <TextField
              id="password"
              name="password"
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="Password"
              placeholder="••••••"
              value={password}
              error={invalid && !password}
              onChange={(event) => setPassword(event.target.value.trim())}
              slotProps={{
                inputLabel: { suppressHydrationWarning: true },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton aria-label="toggle password visibility" onClick={toggleShowPassword}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
          <GoIf value=".Invalid" data={data}>
            <Typography id="invalid-credentials" color="error" textAlign="center">
              Invalid username or password
            </Typography>
          </GoIf>
          <GoIf value=".ResetPasswordURL" data={data}>
            <Stack direction="row" justifyContent="flex-end" sx={{ marginTop: '0 !important' }}>
              <Link
                href={data.ResetPasswordURL!}
                sx={{ color: (theme) => `${theme.vars.palette.primary.main} !important` }}
              >
                Forgot Password ?
              </Link>
            </Stack>
          </GoIf>
          <Box>
            <Button
              id="signin"
              type="submit"
              fullWidth
              disabled={username === '' || password === '' || isSubmitting}
              variant="contained"
            >
              Sign In
            </Button>
          </Box>
          <input hidden readOnly name="id" value={data.ID} />
          <input hidden readOnly name="csrf" value={data.CSRF} />
          <GoIf value=".ExternalProviders" data={data}>
            <Divider>
              <Typography color="textSecondary">or sign in with</Typography>
            </Divider>
            <Grid container sx={{ marginBottom: 2 }} spacing={2}>
              <GoRange value=".ExternalProviders" each={providers} template={ExternalProviderInfoTemplate}>
                {(v) => (
                  <Grid key={v.ID} size={12}>
                    <Button
                      id={`${v.Name}-signin`}
                      data-external-provider-id={v.ID}
                      variant="outlined"
                      fullWidth
                      href={v.URL}
                      sx={{ color: 'inherit' }}
                      startIcon={
                        <Icon sx={{ display: 'flex', width: 20, height: 20 }}>
                          <NoSsr>
                            <SvgIcon component={icons[v.ID as string]} fontSize="small" inheritViewBox />
                          </NoSsr>
                        </Icon>
                      }
                    >
                      {v.Name}
                    </Button>
                  </Grid>
                )}
              </GoRange>
            </Grid>
          </GoIf>
        </Stack>
      </Stack>
    </Layout>
  );
};

export default LoginPage;
